"use client";

import { useCallback, useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { ImagePlaceholder } from "@/components/ui/ImagePlaceholder/ImagePlaceholder";
import { addProductToCart } from "@/lib/cart/addProductToCart";
import { dispatchCartOpen } from "@/lib/cart/cartEvents";
import { extractInternalHref } from "@/lib/utils/extractInternalHref";
import { useT } from "@/lib/i18n/context";
import { toast } from "@/lib/notifications/toast";
import { productCardTranslations } from "./ProductCard.i18n";
import styles from "./ProductCard.module.css";

/**
 * Shape wymagany przez `ProductCard`. Structurally compatible z
 * `CShopCategoryProduct` (i podobnymi typami produktow z innych sklepowych
 * endpointow) - caller moze przekazac dowolny obiekt z tymi polami.
 */
export type ProductCardItem = {
  id: number;
  name: string;
  href: string;
  status: "public" | "temporary_unavailable" | "archive" | "in_set";
  in_stock: boolean;
  badge: "bestseller" | "recomended" | "new" | null;
  category_name: string;
  price_brutto: number | null;
  price_brutto_before_promotion: number | null;
  promocja: boolean;
  lowest_price: number | null;
  picture: {
    thumb_src: string;
    alt: string;
    width: number | null;
    height: number | null;
  } | null;
};

export type ProductCardProps = {
  item: ProductCardItem;
  currency: string;
};

export function ProductCard({ item, currency }: ProductCardProps) {
  const t = useT(productCardTranslations);
  const router = useRouter();
  const href = extractInternalHref(item.href);
  const [adding, setAdding] = useState(false);
  /* Etykieta pod cena: dla non-public status wygrywa mapping enumu (archive /
     in_set / temporary_unavailable). Dla public bez stock'u pokazujemy
     "tymczasowo niedostepny" - parity z karta produktu (CShopCategoryArticle
     `showSoldOutLabel`). */
  const statusLabel = item.status !== "public"
    ? t.status[item.status]
    : !item.in_stock
    ? t.status.temporary_unavailable
    : null;

  const handleAddToCart = useCallback(
    async (e: React.MouseEvent<HTMLButtonElement>) => {
      /* Kafelek to <Link> zawiniety w <li>, przycisk siedzi w footerze karty
         (poza <a>) - preventDefault + stopPropagation zabezpiecza gdyby event
         propagowal do rodzica. */
      e.preventDefault();
      e.stopPropagation();
      if (adding) return;
      setAdding(true);
      const result = await addProductToCart({ id_product: item.id, count: 1 });
      if (result.ok) {
        toast.success(t.addedToCart(item.name));
        /* Odswiez SSR - w_card w headerze zobaczy nowy stan koszyka przez
           `getShopCardId()` (cookie ustawione route handlerem). */
        router.refresh();
        /* Otworz drawer koszyka (WCard w headerze nasluchuje event'u).
           Kolejnosc: refresh -> event; drawer otworzy sie natychmiast,
           swiezy content przyjdzie z SSR i zsynchronizuje sie w useEffect. */
        dispatchCartOpen();
      } else {
        toast.error(result.message ?? t.addToCartFailed);
      }
      setAdding(false);
    },
    [adding, item.id, item.name, router, t],
  );

  const cardBody = (
    <>
      <div className={styles.image}>
        {item.badge && (
          <span className={styles.badge} data-badge={item.badge}>
            {t.badges[item.badge]}
          </span>
        )}
        {item.picture?.thumb_src ? (
          <img
            src={item.picture.thumb_src}
            alt={item.picture.alt || item.name}
            width={item.picture.width ?? undefined}
            height={item.picture.height ?? undefined}
            loading="lazy"
          />
        ) : (
          <ImagePlaceholder />
        )}
      </div>
      <div className={styles.details}>
        <h3 className={styles.name}>{item.name}</h3>
        {item.category_name && (
          <div className={styles.category}>{item.category_name}</div>
        )}
        <ProductPrice item={item} currency={currency} />
        {statusLabel && <div className={styles.status}>{statusLabel}</div>}
      </div>
    </>
  );

  return (
    <li className={styles.card}>
      {href !== null ? (
        <Link href={href} className={styles.link} aria-label={item.name}>
          {cardBody}
        </Link>
      ) : (
        <a href={item.href} className={styles.link} aria-label={item.name}>
          {cardBody}
        </a>
      )}
      {/* Przycisk koszyka wylaczamy gdy:
          - status !== public (temporary_unavailable / archive / in_set)
          - status public ale magazyn wyczerpany (in_stock=false)
          Stan magazynowy w postaci boolean'a `in_stock` przychodzi z backendu
          (getStockSum cachuje batch w $_ENV, wiec petla po items = 1 query). */}
      {item.status === "public" && item.in_stock && (
        <div className={styles.actions}>
          <button
            type="button"
            className={styles.addToCartButton}
            onClick={handleAddToCart}
            disabled={adding}
          >
            {adding ? t.addingToCart : t.addToCart}
          </button>
        </div>
      )}
    </li>
  );
}

function ProductPrice({
  item,
  currency,
}: {
  item: ProductCardItem;
  currency: string;
}) {
  const t = useT(productCardTranslations);
  if (item.price_brutto === null) return null;

  const mainFormatted = t.formatPrice(item.price_brutto, currency);

  if (item.promocja && item.price_brutto_before_promotion !== null) {
    const beforeFormatted = t.formatPrice(
      item.price_brutto_before_promotion,
      currency,
    );
    return (
      <div className={styles.priceWrap}>
        <span className={styles.pricePromo}>
          <span className={styles.priceBefore}>{beforeFormatted}</span>{" "}
          {mainFormatted}
        </span>
        {item.lowest_price !== null && (
          <div className={styles.lowestPrice}>
            {t.lowestPrice(t.formatPrice(item.lowest_price, currency))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={styles.priceWrap}>
      <span className={styles.price}>
        {mainFormatted}
      </span>
    </div>
  );
}
